import { Button, Skeleton } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ArrowLeft, ExternalLink, Pause, Pencil, Play } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";

import { useAdminCampaign, useSetCampaignEnabled } from "@/admin/services";

import { Card } from "../../../components/ui/card";
import { KPICard } from "../../_components/kpi-card";
import { PageHeader } from "../../_components/page-header";

const STATUS_PILL: Record<string, string> = {
  active: "bg-success-soft text-success-foreground",
  scheduled: "bg-primary-soft text-primary",
  paused: "bg-warning-soft text-warning-foreground",
  ended: "bg-surface text-muted-foreground",
};

export const AdminCampaignDetailPage = () => {
  const params = useParams<{ id: string }>();
  const { data, isLoading } = useAdminCampaign(params.id);
  const setEnabled = useSetCampaignEnabled(params.id ?? "");

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Loading…" trail={["Admin", "Campaigns"]} />
        <Skeleton className="h-32 w-full" />
      </>
    );
  }

  const isPaused = data.status === "paused";

  return (
    <>
      <Helmet>
        <title>{data.name} · Admin</title>
      </Helmet>

      <PageHeader
        title={data.name}
        trail={["Admin", "Campaigns"]}
        description={`/${data.slug} · ${data.offerType === "STRIPE_DISCOUNT" ? "Stripe discount" : "Free access"}`}
        actions={
          <>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/campaigns" className="gap-1">
                <ArrowLeft className="size-3.5" />
                Back
              </Link>
            </Button>
            {data.pageSlug && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`/offer/${data.pageSlug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="gap-1"
                >
                  Public page
                  <ExternalLink className="size-3.5" />
                </a>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to={`/campaigns/${data.id}/edit`} className="gap-1">
                <Pencil className="size-3.5" />
                Edit
              </Link>
            </Button>
            {data.status !== "ended" && (
              <Button
                variant={isPaused ? "default" : "outline"}
                size="sm"
                className="gap-1"
                onClick={() => setEnabled.mutate(isPaused)}
                disabled={setEnabled.isPending}
              >
                {isPaused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
                {isPaused ? "Resume" : "Pause"}
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard label="Views" value={data.metrics.views.toLocaleString()} />
        <KPICard label="Claims" value={data.metrics.claims.toLocaleString()} />
        <KPICard label="Conversions" value={data.metrics.conversions.toLocaleString()} />
        <KPICard
          label="Revenue (gross)"
          value={
            data.metrics.grossRevenueCents > 0
              ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                  data.metrics.grossRevenueCents / 100,
                )
              : "$0"
          }
        />
      </div>

      <section className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Schedule">
          <dl className="space-y-2.5">
            <Row label="Starts" value={new Date(data.startsAt).toLocaleString()} />
            <Row label="Ends" value={new Date(data.endsAt).toLocaleString()} />
            <Row label="Audience" value={data.targetAudience} />
            <Row
              label="Cap"
              value={
                data.maxRedemptions
                  ? `${data.currentRedemptions} of ${data.maxRedemptions}`
                  : `${data.currentRedemptions} (uncapped)`
              }
            />
          </dl>
        </Card>

        <Card title="Offer details">
          <dl className="space-y-2.5">
            {data.offerType === "STRIPE_DISCOUNT" ? (
              <>
                <Row label="Discount" value={`${data.discountPercent}%`} />
                <Row label="Duration" value={`${data.discountDurationMonths} month(s)`} />
                <Row label="Stripe coupon" value={data.stripeCouponId ?? "—"} mono />
                <Row label="Plans" value={data.discountPlans.join(", ") || "—"} />
              </>
            ) : (
              <>
                <Row label="Granted plan" value={data.grantedPlan} />
                <Row label="Granted duration" value={`${data.grantedDurationDays} days`} />
              </>
            )}
          </dl>
        </Card>
      </section>

      <Card className="mt-4" title="Copy">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CopyField label="Banner text" value={data.bannerText} />
          <CopyField label="Page headline" value={data.pageHeadline} />
          <CopyField
            className="md:col-span-2"
            label="Page subheadline"
            value={data.pageSubheadline}
          />
        </div>
      </Card>

      <section className="mt-4 flex items-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-[12px]">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
            STATUS_PILL[data.status] ?? STATUS_PILL.ended,
          )}
        >
          {data.status}
        </span>
        <span className="text-muted-foreground">
          Created {new Date(data.createdAt).toLocaleDateString()}
        </span>
      </section>
    </>
  );
};

const Row = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="text-[12px] text-muted-foreground">{label}</dt>
    <dd
      className={cn(
        "text-[13px] tabular-nums text-foreground",
        mono && "mono max-w-[60%] truncate text-[12px] text-muted-foreground",
      )}
      title={mono ? value : undefined}
    >
      {value}
    </dd>
  </div>
);

const CopyField = ({
  label,
  value,
  className,
}: {
  label: string;
  value: string | null;
  className?: string;
}) => (
  <div className={className}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">{label}</p>
    <p className="mt-1.5 whitespace-pre-wrap break-words text-[13px] text-foreground">
      {value ?? <span className="text-subtle">—</span>}
    </p>
  </div>
);

export default AdminCampaignDetailPage;
